/**
 * JWT Token Utilities
 *
 * Utilities for decoding and inspecting JWT tokens without making API calls.
 * JWTs are base64-encoded (not encrypted), so you can read the payload client-side.
 *
 * @example
 * ```tsx
 * import { decodeJwt, isTokenExpired, getTokenExpiresIn } from "@app/utils/jwt-utils";
 *
 * const token = localStorage.getItem("access_token");
 *
 * // Check if expired
 * if (isTokenExpired(token)) {
 *   console.log("Token is expired, need to refresh");
 * }
 *
 * // Get expiration time
 * const expiresIn = getTokenExpiresIn(token);
 * console.log(`Token expires in ${expiresIn} seconds`);
 *
 * // Decode to read claims
 * const payload = decodeJwt(token);
 * console.log(`Token user: ${payload.sub}`);
 * ```
 */

/**
 * JWT Payload interface
 */
export interface JwtPayload {
    exp?: number; // Expiration time (Unix timestamp in seconds)
    iat?: number; // Issued at (Unix timestamp in seconds)
    sub?: string; // Subject (user ID)
    email?: string; // User email
    name?: string; // User name
    roles?: Record<string, string[]>; // User roles by domain
    [key: string]: unknown; // Additional claims
}

/**
 * Decode a JWT token without verifying the signature.
 *
 * ⚠️ IMPORTANT: This only decodes the payload. It does NOT verify the signature.
 * The server will verify the signature when you send the token. Use this only
 * for reading non-sensitive token data like expiration time.
 *
 * JWT structure: `<header>.<payload>.<signature>`
 *
 * @param token - JWT token string
 * @returns Decoded JWT payload object
 * @throws Error if token is invalid or malformed
 *
 * @example
 * ```tsx
 * const token = "eyJhbGc...";
 * const payload = decodeJwt(token);
 * console.log(payload.exp); // Expiration timestamp
 * ```
 */
export function decodeJwt(token: string | null | undefined): JwtPayload {
    if (!token) {
        throw new Error("Token is required");
    }

    // JWT has 3 parts separated by dots: header.payload.signature
    const parts = token.split(".");
    if (parts.length !== 3) {
        throw new Error("Invalid token format. Expected 3 parts separated by dots.");
    }

    try {
        // Decode the payload (second part)
        const payload = parts[1];

        // Add padding if needed (base64 requires length to be multiple of 4)
        const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);

        // Decode from base64 to string
        const decoded = atob(padded);

        // Parse JSON
        return JSON.parse(decoded) as JwtPayload;
    } catch (error) {
        throw new Error(`Failed to decode token: ${error instanceof Error ? error.message : String(error)}`);
    }
}

/**
 * Check if a JWT token is expired.
 *
 * Compares the token's `exp` claim with the current time.
 * Does NOT verify the signature (the server will do that).
 *
 * @param token - JWT token string
 * @param bufferSeconds - Grace period before expiration (default: 0)
 *                        Use this to refresh slightly before actual expiration
 * @returns true if token is expired or expiring soon, false otherwise
 *
 * @example
 * ```tsx
 * if (isTokenExpired(token)) {
 *   console.log("Token expired, refresh needed");
 * }
 *
 * // With 60-second buffer (refresh 1 minute before actual expiration)
 * if (isTokenExpired(token, 60)) {
 *   refreshToken();
 * }
 * ```
 */
export function isTokenExpired(token: string | null | undefined, bufferSeconds = 0): boolean {
    try {
        if (!token) {
            return true; // No token = expired
        }

        const payload = decodeJwt(token);

        if (!payload.exp) {
            // Token doesn't have expiration (unusual but possible)
            return false;
        }

        // Current time in seconds (Unix timestamp)
        const nowSeconds = Math.floor(Date.now() / 1000);

        // Token is expired if: current_time >= (exp_time - buffer)
        return nowSeconds >= payload.exp - bufferSeconds;
    } catch {
        // If we can't decode the token, treat it as expired (invalid)
        return true;
    }
}

/**
 * Get the number of seconds until a token expires.
 *
 * @param token - JWT token string
 * @returns Number of seconds until expiration (negative if already expired)
 * @throws Error if token is invalid
 *
 * @example
 * ```tsx
 * const expiresIn = getTokenExpiresIn(token);
 * if (expiresIn < 60) {
 *   console.log(`Token expires in ${expiresIn} seconds - should refresh soon`);
 * }
 * ```
 */
export function getTokenExpiresIn(token: string | null | undefined): number {
    if (!token) {
        return -1; // Token doesn't exist = already "expired"
    }

    const payload = decodeJwt(token);

    if (!payload.exp) {
        return Infinity; // No expiration
    }

    const nowSeconds = Math.floor(Date.now() / 1000);
    return payload.exp - nowSeconds;
}

/**
 * Get the expiration time as a Date object.
 *
 * @param token - JWT token string
 * @returns Date when token expires
 * @throws Error if token is invalid
 *
 * @example
 * ```tsx
 * const expiresAt = getTokenExpirationDate(token);
 * console.log(`Token expires at: ${expiresAt.toLocaleString()}`);
 * ```
 */
export function getTokenExpirationDate(token: string | null | undefined): Date {
    if (!token) {
        throw new Error("Token is required");
    }

    const payload = decodeJwt(token);

    if (!payload.exp) {
        throw new Error("Token does not have an expiration time");
    }

    // exp is in seconds, Date expects milliseconds
    return new Date(payload.exp * 1000);
}

/**
 * Check if a token will expire within a certain time period.
 *
 * Useful for proactive token refresh before actual expiration.
 *
 * @param token - JWT token string
 * @param thresholdSeconds - How many seconds in the future to check
 * @returns true if token will expire within threshold
 *
 * @example
 * ```tsx
 * // Refresh if token expires within 5 minutes (300 seconds)
 * if (willTokenExpire(token, 300)) {
 *   refreshToken();
 * }
 * ```
 */
export function willTokenExpire(token: string | null | undefined, thresholdSeconds: number): boolean {
    try {
        const expiresIn = getTokenExpiresIn(token);
        return expiresIn < thresholdSeconds;
    } catch {
        return true; // Invalid token = treat as expiring soon
    }
}
